import ApiService from '@/services/api.service.js';
import CompanyService from '@/services/company.service.js';
import logger from '@/utils/logger';

/**
 * Export vendors to Excel file
 * @param {Object} options - Export options
 * @param {Array} options.vendors - List of vendors to export
 * @param {string|number} options.companyId - Company ID
 * @param {string} options.currentView - Current view filter ('Active', 'Inactive', or 'All')
 * @param {Function} options.getVendorMonthlyAmount - Function to get vendor monthly amount
 * @param {Function} options.getVendorYearToDate - Function to get vendor year to date
 * @param {Function} options.showToast - Function to show toast notifications (optional)
 * @returns {Promise<void>}
 */
export async function exportVendorsToExcel({
  vendors,
  companyId,
  currentView,
  getVendorMonthlyAmount,
  getVendorYearToDate,
  showToast
}) {
  if (!vendors || vendors.length === 0) {
    if (showToast) showToast('warning', 'No vendors to export');
    return;
  }

  try {
    // Show loading message
    if (showToast) showToast('info', 'Preparing export with vendor details and addresses...');

    // Dynamically import xlsx library
    const XLSX = await import('xlsx');

    // Fetch vendor details with related data
    const vendorsWithDetails = await Promise.all(
      vendors.map(async (vendor) => {
        try {
          const vendorId = vendor.id || vendor.vendorId;
          
          // Fetch full vendor details with addresses and contacts
          let vendorDetails = vendor;
          try {
            const vendorResponse = await ApiService.get(`/vendor/vendors/${vendorId}`);
            if (vendorResponse && vendorResponse.data) {
              vendorDetails = vendorResponse.data;
            }
          } catch (err) {
            logger.warn(`Could not fetch full details for vendor ${vendorId}, using existing data`);
          }

          // Extract addresses (may already be in vendorDetails from getAllVendors)
          const addresses = vendorDetails.addresses || [];
          const billingAddress = addresses.find(addr => addr.type === 'Billing') || {};
          const shippingAddress = addresses.find(addr => addr.type === 'Shipping') || {};
          const otherAddress = addresses.find(addr => addr.type === 'Other') || {};

          // Extract contacts
          const contacts = vendorDetails.contacts || [];
          const primaryContact = contacts.find(contact => contact.primary) || contacts[0] || {};

          // Fetch payment term
          let paymentTerm = {};
          if (vendorDetails.paymentTermId) {
            try {
              const paymentTermResponse = await ApiService.get(`/company/payment-terms/${vendorDetails.paymentTermId}?companyId=${companyId}`);
              if (paymentTermResponse && paymentTermResponse.data) {
                paymentTerm = paymentTermResponse.data;
              }
            } catch (err) {
              // Ignore errors
            }
          }

          // Fetch vendor group
          let vendorGroup = {};
          if (vendorDetails.vendorGroupId) {
            try {
              const groups = await CompanyService.getAllVendorGroups(companyId);
              vendorGroup = groups.find(g => g.id === vendorDetails.vendorGroupId) || {};
            } catch (err) {
              // Ignore errors
            }
          }

          // Fetch preferred shipping method
          let shippingMethod = {};
          if (vendorDetails.shippingMethodId) {
            try {
              const shippingMethodsResponse = await ApiService.get(`/vendor/preferred-shipping-methods?companyId=${companyId}`);
              const shippingMethods = Array.isArray(shippingMethodsResponse.data) ? shippingMethodsResponse.data : [];
              shippingMethod = shippingMethods.find(sm => sm.id === vendorDetails.shippingMethodId) || {};
            } catch (err) {
              // Ignore errors
            }
          }

          // Fetch account
          let account = {};
          if (vendorDetails.accountId) {
            try {
              const accountResponse = await ApiService.getCompanyAccountById(vendorDetails.accountId);
              if (accountResponse && accountResponse.data) {
                account = accountResponse.data;
              }
            } catch (err) {
              // Ignore errors
            }
          }

          return {
            vendor: vendorDetails,
            billingAddress,
            shippingAddress,
            otherAddress,
            primaryContact,
            paymentTerm,
            vendorGroup,
            shippingMethod,
            account
          };
        } catch (err) {
          logger.error(`Error fetching details for vendor ${vendor.vendorName}:`, err);
          return {
            vendor,
            billingAddress: {},
            shippingAddress: {},
            otherAddress: {},
            primaryContact: {},
            paymentTerm: {},
            vendorGroup: {},
            shippingMethod: {},
            account: {}
          };
        }
      })
    );

    // Check which data fields are available
    const hasBillingAddress = vendorsWithDetails.some(v => v.billingAddress && v.billingAddress.address1);
    const hasShippingAddress = vendorsWithDetails.some(v => v.shippingAddress && v.shippingAddress.address1);
    const hasOtherAddress = vendorsWithDetails.some(v => v.otherAddress && v.otherAddress.address1);
    const hasContactData = vendorsWithDetails.some(v => v.primaryContact && v.primaryContact.name);
    const hasPaymentTermData = vendorsWithDetails.some(v => v.paymentTerm && v.paymentTerm.name);
    const hasVendorGroupData = vendorsWithDetails.some(v => v.vendorGroup && v.vendorGroup.name);
    const hasShippingMethodData = vendorsWithDetails.some(v => v.shippingMethod && v.shippingMethod.name);
    const hasAccountData = vendorsWithDetails.some(v => v.account && v.account.accountName);

    // Check which monthly columns have data
    const monthlyColumns = [
      { key: 'jan', label: 'January' },
      { key: 'feb', label: 'February' },
      { key: 'mar', label: 'March' },
      { key: 'apr', label: 'April' },
      { key: 'may', label: 'May' },
      { key: 'jun', label: 'June' },
      { key: 'jul', label: 'July' },
      { key: 'aug', label: 'August' },
      { key: 'sep', label: 'September' },
      { key: 'oct', label: 'October' },
      { key: 'nov', label: 'November' },
      { key: 'dec', label: 'December' }
    ];

    const hasMonthlyData = {};
    monthlyColumns.forEach(month => {
      hasMonthlyData[month.key] = vendorsWithDetails.some(({ vendor }) => {
        const vendorId = vendor.id || vendor.vendorId;
        return getVendorMonthlyAmount(vendorId, month.key) !== 0;
      });
    });

    const hasYearToDate = vendorsWithDetails.some(({ vendor }) => {
      const vendorId = vendor.id || vendor.vendorId;
      return getVendorYearToDate(vendorId) !== 0;
    });

    // Prepare export data
    const exportData = vendorsWithDetails.map(({ vendor, billingAddress, shippingAddress, otherAddress, primaryContact, paymentTerm, vendorGroup, shippingMethod, account }) => {
      const vendorId = vendor.id || vendor.vendorId;
      const row = {
        // Basic Info
        'Active': vendor.active !== false ? 'Yes' : 'No',
        'Vendor ID': vendor.vendorId || '',
        'Vendor Name': vendor.vendorName || vendor.name || '',
        'Vendor Since': vendor.vendorSince ? new Date(vendor.vendorSince).toLocaleDateString() : '',
        'Tax ID': vendor.taxId || '',
        'Fax': vendor.fax || '',
        'Webpage': vendor.webpage || '',
        'Notes': vendor.notes || '',
        'Opening Balance': vendor.openingBalance != null ? Number(vendor.openingBalance) : 0,
        'Balance': vendor.balance != null ? Number(vendor.balance) : 0,
        'Balance As Of': vendor.balanceAsOf ? new Date(vendor.balanceAsOf).toLocaleDateString() : '',
        'Credit Limit': vendor.creditLimit != null ? Number(vendor.creditLimit) : 0,
        'Payment Method': vendor.paymentMethod || '',
        'Bank Name': vendor.bankName || '',
        'Account Number': vendor.accountNumber || '',
        'Bank Account Type': vendor.bankAccountType || '',
        'Bank Routing Number': vendor.bankRoutingNumber || '',
        'Bank Branch': vendor.bankBranch || '',
        'Tax Registration Number': vendor.taxRegistrationNumber || '',
        'Tax Code': vendor.taxCode || '',
        'Service Tax Registration Number': vendor.serviceTaxRegistrationNo || ''
      };

      // Add monthly data only if at least one vendor has data for that month
      monthlyColumns.forEach(month => {
        if (hasMonthlyData[month.key]) {
          row[month.label] = getVendorMonthlyAmount(vendorId, month.key);
        }
      });

      if (hasYearToDate) {
        row['Year to Date'] = getVendorYearToDate(vendorId);
      }

      // Add related data if available
      if (hasPaymentTermData) {
        row['Payment Terms'] = paymentTerm.name || '';
      }
      if (hasVendorGroupData) {
        row['Vendor Group'] = vendorGroup.name || '';
      }
      if (hasShippingMethodData) {
        row['Preferred Shipping Method'] = shippingMethod.name || '';
      }
      if (hasAccountData) {
        row['Account'] = account.accountName || '';
      }

      // Add contact data if available
      if (hasContactData) {
        row['Contact Name'] = primaryContact.name || '';
        row['Contact Title'] = primaryContact.title || '';
        row['Contact Phone'] = primaryContact.phone || '';
        row['Contact Email'] = primaryContact.email || '';
      }

      // Add billing address if available
      if (hasBillingAddress) {
        row['Billing Address Type'] = billingAddress.type || '';
        row['Billing Address 1'] = billingAddress.address1 || '';
        row['Billing Address 2'] = billingAddress.address2 || '';
        row['Billing City'] = billingAddress.city || '';
        row['Billing State'] = billingAddress.state || '';
        row['Billing Postal Code'] = billingAddress.postalCode || '';
        row['Billing Country'] = billingAddress.country || '';
      }

      // Add shipping address if available
      if (hasShippingAddress) {
        row['Shipping Address Type'] = shippingAddress.type || '';
        row['Shipping Address 1'] = shippingAddress.address1 || '';
        row['Shipping Address 2'] = shippingAddress.address2 || '';
        row['Shipping City'] = shippingAddress.city || '';
        row['Shipping State'] = shippingAddress.state || '';
        row['Shipping Postal Code'] = shippingAddress.postalCode || '';
        row['Shipping Country'] = shippingAddress.country || '';
      }

      // Add other address if available
      if (hasOtherAddress) {
        row['Other Address Type'] = otherAddress.type || '';
        row['Other Address 1'] = otherAddress.address1 || '';
        row['Other Address 2'] = otherAddress.address2 || '';
        row['Other City'] = otherAddress.city || '';
        row['Other State'] = otherAddress.state || '';
        row['Other Postal Code'] = otherAddress.postalCode || '';
        row['Other Country'] = otherAddress.country || '';
      }

      return row;
    });

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths (will be adjusted based on actual columns)
    const defaultWidth = 15;
    const cols = Object.keys(exportData[0] || {}).map(() => ({ wch: defaultWidth }));
    ws['!cols'] = cols;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vendors');

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const statusFilter = currentView === 'Active' ? 'Active' : currentView === 'Inactive' ? 'Inactive' : 'All';
    const filenameParts = ['Vendors', statusFilter, 'Details'];
    if (hasBillingAddress || hasShippingAddress || hasOtherAddress) filenameParts.push('WithAddresses');
    if (hasContactData) filenameParts.push('WithContacts');
    if (hasYearToDate || Object.values(hasMonthlyData).some(hasData => hasData)) filenameParts.push('WithSales');
    filenameParts.push(timestamp);
    const filename = `${filenameParts.join('_')}.xlsx`;

    // Write workbook to binary string
    const wbout = XLSX.write(wb, { 
      bookType: 'xlsx', 
      type: 'array',
      bookSST: false
    });

    // Create blob with proper MIME type
    const blob = new Blob([wbout], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    // Append to body before clicking
    document.body.appendChild(link);
    
    // Trigger download
    link.click();
    
    // Clean up after a short delay
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    }, 100);

    // Show success message
    const includedData = [];
    if (hasPaymentTermData) includedData.push('payment terms');
    if (hasVendorGroupData) includedData.push('vendor groups');
    if (hasBillingAddress || hasShippingAddress || hasOtherAddress) includedData.push('addresses');
    if (hasContactData) includedData.push('contacts');
    if (hasYearToDate || Object.values(hasMonthlyData).some(hasData => hasData)) includedData.push('sales data');
    
    const message = includedData.length > 0 
      ? `Exported ${exportData.length} vendor(s) with ${includedData.join(', ')}`
      : `Exported ${exportData.length} vendor(s)`;
    
    if (showToast) showToast('success', message);
  } catch (err) {
    logger.error('Error exporting vendors to Excel:', err);
    if (showToast) showToast('error', 'Failed to export vendors to Excel');
    throw err;
  }
}

